import { brand } from '@brand';

import VerifiedIcon from '@mui/icons-material/Verified';
import { AppBar, Box, Toolbar, Tooltip, Typography } from '@mui/material';

import type { SenderOrganisation } from '@/types';

interface RevealHeaderProps {
    /** the sender's organisation, only sent for public ones with a verified domain */
    sender?: SenderOrganisation | null;
}

/**
 * The bar at the top of every reveal screen: the brand's logo on the blue the web app's sidebar and
 * the mobile app's headers use, the same in light and dark. The logo opens the brand's own site in a
 * new tab, so whoever is opening a secret does not leave it. When the sender's organisation has
 * verified its domain, the right side says who sent it ("Shared by Acme · acme.com"); the page never
 * shows a name the backend did not vouch for.
 *
 * On a SharePass One instance the logo is the client's: whoever opens the link has no account and
 * may never have heard of us, so our name here would tell them who really runs it.
 */
export const RevealHeader = ({ sender }: RevealHeaderProps) => {
    const name = sender?.name?.trim();
    const domain = sender?.domain?.trim();

    return (
        <AppBar
            position="static"
            elevation={0}
            sx={{
                background: 'linear-gradient(to right, #1976D2, #0052CC)',
                color: '#FFFFFF',
            }}
        >
            {/* The site's column (--container 1200, --gutter 24), so the logo sits above the
                page's own content rather than out at the window's edge, as it does on the site. */}
            <Toolbar
                sx={{
                    minHeight: { xs: 56, sm: 64 },
                    width: '100%',
                    maxWidth: '1200px',
                    mx: 'auto',
                    px: '24px',
                    gap: 2,
                }}
            >
                <Box
                    component="a"
                    href={brand.homeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${brand.name} (opens in a new tab)`}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        borderRadius: 1,
                        '&:focus-visible': { outline: '2px solid #FFFFFF', outlineOffset: 4 },
                    }}
                >
                    <Box
                        component="img"
                        src={brand.logo.onDark}
                        alt={brand.name}
                        sx={{
                            height: { xs: Math.round(brand.logo.barHeight * 0.87), sm: brand.logo.barHeight },
                            display: 'block',
                        }}
                    />
                </Box>

                {name && (
                    <Tooltip
                        title={domain ? `${name} has verified that it owns ${domain}.` : ''}
                        arrow
                        placement="bottom-end"
                    >
                        <Box
                            data-testid="reveal-sender"
                            sx={{
                                ml: 'auto',
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                            }}
                        >
                            <VerifiedIcon sx={{ fontSize: 18, flexShrink: 0 }} aria-hidden />
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: { xs: 13, sm: 14 },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Shared by <Box component="strong">{name}</Box>
                                {domain && (
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, opacity: 0.8 }}>
                                        {' · '}
                                        {domain}
                                    </Box>
                                )}
                            </Typography>
                        </Box>
                    </Tooltip>
                )}
            </Toolbar>
        </AppBar>
    );
};
